import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/auth-context';
import PackagesAPI from '../../../services/api/admin/packages-api';
import Logger from '../../../utils/logger';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';

export const UpdateBudgetModal = ({
  open,
  packageSelected,
  handleCancel,
  handleUpdateBudgetComplete,
}) => {
  const auth = useContext(AuthContext);

  const [fieldHrs, setFieldHrs] = useState('');
  const [shopHrs, setShopHrs] = useState('');
  const [changeReason, setChangeReason] = useState('');
  const [coReference, setCoReference] = useState('');
  const [notes, setNotes] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reset/populate form state on packageSelected changes
  useEffect(() => {
    if (open && packageSelected) {
      setFieldHrs(
        packageSelected.field_hrs_budgeted !== null &&
          packageSelected.field_hrs_budgeted !== undefined
          ? Number(packageSelected.field_hrs_budgeted).toFixed(2)
          : ''
      );
      setShopHrs(
        packageSelected.shop_hrs_budgeted !== null &&
          packageSelected.shop_hrs_budgeted !== undefined
          ? Number(packageSelected.shop_hrs_budgeted).toFixed(2)
          : ''
      );
      setChangeReason('');
      setCoReference('');
      setNotes('');
      setErrorMsg('');
    }
  }, [open, packageSelected]);

  if (!packageSelected) return null;

  // Parsed input values
  const currentFieldHrs =
    packageSelected.field_hrs_budgeted !== null
      ? parseFloat(packageSelected.field_hrs_budgeted)
      : 0;
  const currentShopHrs =
    packageSelected.shop_hrs_budgeted !== null
      ? parseFloat(packageSelected.shop_hrs_budgeted)
      : 0;
  const currentTotalHrs = currentFieldHrs + currentShopHrs;

  const parsedFieldHrs = fieldHrs !== '' ? parseFloat(fieldHrs) : 0;
  const parsedShopHrs = shopHrs !== '' ? parseFloat(shopHrs) : 0;
  const newTotalHrs = parsedFieldHrs + parsedShopHrs;
  const deltaHrs = newTotalHrs - currentTotalHrs;

  // Validation rules
  const isFieldChanged = fieldHrs !== '' && parsedFieldHrs !== currentFieldHrs;
  const isShopChanged = shopHrs !== '' && parsedShopHrs !== currentShopHrs;
  const isAnyHrsChanged = isFieldChanged || isShopChanged;

  const isHrsValid =
    !isNaN(parsedFieldHrs) &&
    parsedFieldHrs >= 0 &&
    !isNaN(parsedShopHrs) &&
    parsedShopHrs >= 0;

  const isReasonValid =
    changeReason !== '' &&
    (changeReason !== 'Change Order' || coReference.trim() !== '');

  const isSaveDisabled =
    !isAnyHrsChanged || !isHrsValid || !isReasonValid || submitting;

  const handleSave = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMsg('');

    const payload = {
      field_hrs_budgeted: fieldHrs !== '' ? parsedFieldHrs : null,
      shop_hrs_budgeted: shopHrs !== '' ? parsedShopHrs : null,
      change_reason: changeReason,
      change_order_reference:
        changeReason === 'Change Order' ? coReference.trim() : null,
      notes: notes.trim() || null,
    };

    Logger.userAction('Submitting budget update', {
      packageId: packageSelected.id,
      payload,
    });

    try {
      const data = await PackagesAPI.updateBudget(
        packageSelected.id,
        payload,
        auth.token
      );
      Logger.info('Package budget updated successfully', {
        packageId: packageSelected.id,
      });

      // Notify parent grid of changes to refresh local state
      handleUpdateBudgetComplete({
        id: packageSelected.id,
        field_hrs_budgeted: payload.field_hrs_budgeted,
        shop_hrs_budgeted: payload.shop_hrs_budgeted,
        total_hrs: data.package.total_hrs,
        earned_field_hrs: data.package.earned_field_hrs,
        earned_shop_hrs: data.package.earned_shop_hrs,
        total_earned_hrs: data.package.total_earned_hrs,
        previous_total_hrs: currentTotalHrs,
      });

      handleCancel();
    } catch (err) {
      Logger.error('Error updating package budget', err);
      const msg =
        err.response?.data?.message ||
        err.message ||
        'Error updating package budget';
      setErrorMsg(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleCancel} fullWidth maxWidth="xs">
      <DialogTitle>Update Budgeted Hours</DialogTitle>
      <form onSubmit={handleSave}>
        <DialogContent
          sx={{
            pt: 1,
            pb: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              {packageSelected.autodesk_name
                ? `${packageSelected.autodesk_name} — `
                : ''}
              {packageSelected.name}
            </Typography>
          </Box>

          <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary" align="center">
              Current: Field <strong>{currentFieldHrs.toFixed(1)}</strong> |
              Shop <strong>{currentShopHrs.toFixed(1)}</strong> | Total{' '}
              <strong>{currentTotalHrs.toFixed(1)}</strong>
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              required
              fullWidth
              id="field_hours"
              label="Field Hours"
              type="number"
              inputProps={{ min: 0, step: 0.01 }}
              value={fieldHrs}
              onChange={(e) => setFieldHrs(e.target.value)}
            />
            <TextField
              required
              fullWidth
              id="shop_hours"
              label="Shop Hours"
              type="number"
              inputProps={{ min: 0, step: 0.01 }}
              value={shopHrs}
              onChange={(e) => setShopHrs(e.target.value)}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 1,
            }}
          >
            <Typography variant="body2">
              New Total: <strong>{newTotalHrs.toFixed(1)}</strong>
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              &Delta;{' '}
              {deltaHrs >= 0 ? `+${deltaHrs.toFixed(1)}` : deltaHrs.toFixed(1)}
            </Typography>
          </Box>

          <FormControl fullWidth required>
            <InputLabel id="change-reason-label">Reason</InputLabel>
            <Select
              labelId="change-reason-label"
              id="change_reason"
              value={changeReason}
              label="Reason"
              onChange={(e) => setChangeReason(e.target.value)}
            >
              <MenuItem value="Original Budget">Original Budget</MenuItem>
              <MenuItem value="Change Order">Change Order</MenuItem>
              <MenuItem value="Budget Correction">Budget Correction</MenuItem>
              <MenuItem value="Transfer Between Packages">
                Transfer Between Packages
              </MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>

          {changeReason === 'Change Order' && (
            <TextField
              required
              fullWidth
              id="co_reference"
              label="CO Reference"
              type="text"
              value={coReference}
              onChange={(e) => setCoReference(e.target.value)}
            />
          )}

          {changeReason === 'Transfer Between Packages' && (
            <Alert severity="info" sx={{ py: 0.5 }}>
              Remember to make the matching change on the other package.
            </Alert>
          )}

          <TextField
            fullWidth
            id="notes"
            label="Notes"
            type="text"
            multiline
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />

          {errorMsg && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {errorMsg}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={isSaveDisabled}>
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
